/**
 * The deadlines, as one table.
 *
 * `5c`'s whole argument is a layout argument, and it is right: five separate date fields with two
 * lock checkboxes scattered between them make a commissioner hunt for the one he came to change.
 * A row per deadline lets him read down a column instead. Nothing about WHEN anything happens
 * moves — `deadlines.ts` still owns that — this only decides what each row says about itself.
 *
 * The part worth having in the domain rather than in JSX is the fourth column. The drawing calls
 * it the row's live consequence, and every row answers a different question with it:
 *
 *   a gate that people submit to  → HOW MANY HAVE COMPLIED. "0 of 10 submitted" is the only fact
 *                                   that tells a commissioner whether to chase anybody.
 *   a gate nobody submits to      → the clock in the other zone, because the row above it is in
 *                                   Eastern and half the league is not.
 *   a date that locks nothing     → SAY SO IN PLAIN WORDS. This read "a date, not a gate", which
 *                                   rhymes, uses a word no manager uses, and takes a second read
 *                                   to decode (Tim, 31 August: "don't be cute like this with
 *                                   wording in the app"). What the row has to stop is somebody
 *                                   waiting for the app to do something on the 26th that it was
 *                                   never going to do, and the way to stop that is to tell him.
 *
 * The two-control rule survives intact and is the reason `override` is its own column: the date
 * is the announcement, the lock is the override. Collapsing them would force a commissioner to
 * rewrite an announced time in order to reopen one man's slate.
 */

import {
  bothZones, clockIn, gateFor, timeLeft, urgencyOf, ZONES,
  type Gate, type GateException, type GateSource, type Urgency,
} from './deadlines.js';

/** Every deadline the app holds, in the order the drawing lists them. */
export type DeadlineKey = 'keepers' | 'preferences' | 'proposals' | 'committee' | 'draft';

export interface DeadlineFacts {
  /** How many managers have declared a keeper slate, and how many there are. */
  declared?: { of: number; done: number };
  /** How many have submitted slot preferences. */
  preferences?: { of: number; done: number };
  /** How many proposals are on the published docket. */
  onDocket?: number;
}

export interface DeadlineSource extends GateSource {
  committeeDate?: string | null;
  draftDate?: string | null;
}

export interface DeadlineRow {
  key: DeadlineKey;
  label: string;
  /** The instant, or null where nothing has been set. */
  iso: string | null;
  /**
   * Whether the app REFUSES WRITES after this moment. A gate gets a status pill and an override
   * control; a date gets neither, because there is nothing to open or close.
   */
  gates: boolean;
  /** Null on a row that gates nothing. */
  gate: Gate | null;
  locked: boolean;
  /** Set while a commissioner is holding the gate open past its date. Null otherwise. */
  heldOpen: GateException | null;
  /** `Open` / `Closed`, or empty on a date. */
  status: string;
  /** The fourth column: what this row is doing right now, in one line. */
  note: string;
  /**
   * The note WITHOUT its coarse countdown — "0 of 10 declared", "the app locks nothing on this
   * date".
   *
   * Home's rail carries a live clock in the pill beside the label, and "29 min · offline" under a
   * ticking `0:29:56` is the same fact twice in two precisions. The table on League controls still
   * wants both in one cell, so both are published and each screen takes the half it needs.
   */
  fact: string;
  /** Inside a day, or already shut. Drives the colour, as everywhere else. */
  urgent: boolean;
  /**
   * HOW CLOSE, in four bands rather than two.
   *
   * `urgent` above is a colour decision and conflates "inside a day" with "already gone", which
   * is right for a chip and wrong for deciding what a page leads with. Home sorts and promotes on
   * this; the table still colours on `urgent`, so nothing about this screen changed when it was
   * added.
   */
  urgency: Urgency;
}

const LABEL: Record<DeadlineKey, string> = {
  keepers: 'Keeper declarations',
  preferences: 'Draft slot preferences',
  proposals: 'Rule proposals',
  committee: 'Committee meeting',
  /*
   * "Draft day", not "draft night" — the phase strip calls it that since 23 August and one name
   * for one thing is worth more than the fact that it starts at 8pm. The prose elsewhere in the
   * app still says draft night, which is what people call the evening; this is a row label in a
   * table of dates and has to match the stop on the strip.
   */
  draft: 'Draft day',
};

/** The clock in the league's second zone — "10:00 AM MT". The date column already said the day. */
function otherZone(iso: string): string {
  const second = ZONES[1];
  return second ? `${clockIn(iso, second.tz)} ${second.label}` : '';
}

/** "16 days" — the coarse count, without the "left" that `timeLeft` does not add either. */
const away = (iso: string, now: number) => {
  const t = timeLeft(iso, now);
  return t === 'passed' ? 'passed' : t;
};

export function deadlineRows(
  s: DeadlineSource | null | undefined,
  facts: DeadlineFacts = {},
  now = Date.now(),
): DeadlineRow[] {
  const row = (
    key: DeadlineKey,
    iso: string | null | undefined,
    opts: {
      gates: boolean; locked?: boolean; heldOpen?: GateException | null; what?: string;
      compliance?: string | null;
    },
  ): DeadlineRow => {
    const at = iso ?? null;
    const gate = opts.gates
      ? gateFor(at, opts.locked ?? false, now, opts.what, opts.heldOpen ?? null) : null;
    /* The row reports the exception only while it stands; a spent one is a null, not a memory. */
    const heldOpen = gate?.exception ?? null;

    /*
     * An unset deadline is a real state and says so rather than borrowing the shape of a set one.
     * Blank beats partial, as everywhere else in this app.
     */
    if (!at) {
      return {
        key, label: LABEL[key], iso: null, gates: opts.gates, gate,
        locked: opts.locked ?? false,
        heldOpen,
        status: opts.gates ? 'Open' : '',
        note: 'not set',
        fact: 'not set',
        urgent: false,
        urgency: 'far',
      };
    }

    /*
     * Days first, then the fact — except once the moment has passed, when "passed · 4 on the
     * docket" is the pill in the column to the left saying `Closed` twice. The drawing shows a
     * different fact on every row; the app can compute both, so it shows the count where there is
     * one and the second zone's clock where there is not.
     */
    const parts: string[] = [];
    if (!gate?.passed) parts.push(away(at, now));
    parts.push(opts.compliance ?? otherZone(at));

    return {
      key, label: LABEL[key], iso: at, gates: opts.gates, gate,
      locked: opts.locked ?? false,
      heldOpen,
      /* "Open · exception" is the pill's job to say; the status word stays a status word. */
      status: opts.gates ? (gate!.open ? 'Open' : 'Closed') : '',
      note: parts.filter(Boolean).join(' · '),
      fact: opts.compliance ?? otherZone(at),
      urgent: opts.gates ? gate!.urgent : false,
      /*
       * A DATE THAT GATES NOTHING IS STILL CLOSE. The committee meeting and draft night refuse no
       * writes, so they have no `Gate` and no `urgent` — but "the draft is tomorrow" is exactly
       * the fact Home is meant to lead with, and reading urgency off the gate would have made the
       * two loudest days of the year the two quietest rows.
       */
      urgency: urgencyOf(at, now),
    };
  };

  const d = facts.declared;
  const p = facts.preferences;

  return [
    row('keepers', s?.keeperDeadline, {
      gates: true,
      locked: s?.declarationsLocked ?? false,
      heldOpen: s?.declarationsHeldOpen ?? null,
      what: 'Declarations',
      compliance: d ? `${d.done} of ${d.of} declared` : null,
    }),
    row('preferences', s?.preferenceDeadline, {
      gates: true,
      locked: s?.preferencesLocked ?? false,
      heldOpen: s?.preferencesHeldOpen ?? null,
      what: 'Preferences',
      compliance: p ? `${p.done} of ${p.of} submitted` : null,
    }),
    row('proposals', s?.proposalDeadline, {
      gates: true,
      locked: s?.proposalsLocked ?? false,
      heldOpen: s?.proposalsHeldOpen ?? null,
      what: 'Proposals',
      compliance: facts.onDocket === undefined
        ? null
        : `${facts.onDocket} on the docket`,
    }),
    /*
     * Neither of these two refuses a write, and both say so in the same words, because it is the
     * same fact — a row in a table of deadlines looks like a deadline whether or not anything is
     * behind it, and a reader should not have to work out which kind he is looking at.
     */
    row('committee', s?.committeeDate, {
      gates: false, compliance: 'the app locks nothing on this date',
    }),
    row('draft', s?.draftDate, { gates: false, compliance: 'the app locks nothing on this date' }),
  ];
}

/** The full both-zones sentence for a row, for the hint under the date. */
export const rowWhen = (r: DeadlineRow): string => (r.iso ? bothZones(r.iso) : 'Not set');
